/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useState, type ComponentProps } from 'react';

/* Import components */
import CurrentValuePreview from '@/components/charts/CurrentValuePreview';
import DemoSensorDeviceSelection from '@/components/tabs/demo/DemoSensorDeviceSelection';
import DemoSelectedSensorTargets from '@/components/tabs/demo/DemoSelectedSensorTargets';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import types */
import type { CurrentValueOutputState, CurrentValueTimestampMode } from '@/types/ui';

type DemoSensorDeviceSelectionProps = ComponentProps<typeof DemoSensorDeviceSelection>;

interface CurrentValueTabProps extends DemoSensorDeviceSelectionProps {
    currentValueTarget: string;
    currentValueTargets: string[];
    currentValueTimestamp: string;
    currentValueTimestampMode: CurrentValueTimestampMode;
    hasSession: boolean;
    isRequestingCurrentValue: boolean;
    onAddCurrentValueTarget: (target: string) => void;
    onRequestCurrentValue: () => void;
    onRemoveCurrentValueTarget: (target: string) => void;
    onTimeSeriesToChange: (value: string) => void;
    onTimeSeriesToModeChange: (value: CurrentValueTimestampMode) => void;
    outputState: CurrentValueOutputState;
}

function CurrentValueTab({
    currentValueTarget,
    currentValueTargets,
    currentValueTimestamp,
    currentValueTimestampMode,
    hasSession,
    isRequestingCurrentValue,
    onAddCurrentValueTarget,
    onRequestCurrentValue,
    onRemoveCurrentValueTarget,
    onTimeSeriesToChange,
    onTimeSeriesToModeChange,
    outputState,
    ...selectionProps
}: CurrentValueTabProps) {
    const { t } = useI18n();
    const [editableCurrentValueTarget, setEditableCurrentValueTarget] = useState(currentValueTarget);
    const hasCurrentValueTargets = currentValueTargets.length > 0;

    useEffect(() => {
        setEditableCurrentValueTarget(currentValueTarget);
    }, [currentValueTarget]);

    return (
        <div className="flex flex-col gap-6">
            <DemoSensorDeviceSelection {...selectionProps} />

            {selectionProps.selectedDevice ? (
                <>
                    <section className="dashboard-panel flex items-stretch justify-start p-5">
                        <dl className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                    {t('Customer')}
                                </dt>
                                <dd className="mt-1 break-words text-sm text-base-content">
                                    {selectionProps.customer === 'muenster' ? t('Münster') : t('Neutraubling')}
                                </dd>
                            </div>
                            <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                    {t('URN')}
                                </dt>
                                <dd className="mt-1">
                                    <input
                                        id="current-value-target"
                                        className="input input-bordered w-full font-mono text-sm"
                                        type="text"
                                        value={editableCurrentValueTarget}
                                        onChange={(event) => setEditableCurrentValueTarget(event.target.value)}
                                        spellCheck={false}
                                    />
                                </dd>
                            </div>
                        </dl>
                    </section>

                    <div>
                        <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => onAddCurrentValueTarget(editableCurrentValueTarget)}
                            disabled={!hasSession || !editableCurrentValueTarget.trim()}
                        >
                            {t('Add sensor')}
                        </button>
                    </div>

                    <DemoSelectedSensorTargets
                        targets={currentValueTargets}
                        onRemove={onRemoveCurrentValueTarget}
                    />

                    <section className="dashboard-panel flex items-stretch justify-start p-5">
                        <div className="grid w-full grid-cols-1 gap-y-4 text-left md:grid-cols-2 md:divide-x md:divide-base-content">
                            <div className="flex flex-col gap-2 md:pr-5">
                                <label className="text-sm font-semibold" htmlFor="current-value-timestamp-mode">
                                    {t('Date type')}
                                </label>
                                <select
                                    id="current-value-timestamp-mode"
                                    className="select select-bordered w-full"
                                    value={currentValueTimestampMode}
                                    onChange={(event) => onTimeSeriesToModeChange(event.target.value as CurrentValueTimestampMode)}
                                    disabled={!hasCurrentValueTargets}
                                >
                                    <option value="now">{t('Now')}</option>
                                    <option value="custom">{t('Custom date')}</option>
                                    <option value="none">{t('None')}</option>
                                </select>
                            </div>
                            <div className="flex flex-col gap-2 md:pl-5">
                                <label className="text-sm font-semibold" htmlFor="current-value-timestamp">
                                    {t('Date')}
                                </label>
                                <input
                                    id="current-value-timestamp"
                                    className="input input-bordered w-full"
                                    type="datetime-local"
                                    value={currentValueTimestamp}
                                    onChange={(event) => onTimeSeriesToChange(event.target.value)}
                                    disabled={!hasCurrentValueTargets || currentValueTimestampMode !== 'custom'}
                                />
                            </div>
                        </div>
                    </section>

                    <div>
                        <button
                            className="btn btn-primary"
                            type="button"
                            onClick={onRequestCurrentValue}
                            disabled={!hasSession || isRequestingCurrentValue || !hasCurrentValueTargets}
                        >
                            {isRequestingCurrentValue
                                ? currentValueTargets.length > 1 ? t('Requesting current values...') : t('Requesting current value...')
                                : currentValueTargets.length > 1 ? t('Request Current Values') : t('Request Current Value')}
                        </button>
                    </div>

                    {outputState.outputMode === 'currentvalue' && outputState.currentValuePreview ? (
                        <CurrentValuePreview preview={outputState.currentValuePreview} />
                    ) : (
                        <section className="dashboard-panel flex items-stretch justify-start p-5">
                            <div className="flex w-full flex-col gap-2">
                                <label className="text-sm font-semibold" htmlFor="current-value-response-output">
                                    {t('Current value output')}
                                </label>
                                <textarea
                                    id="current-value-response-output"
                                    className="textarea textarea-bordered response-output w-full"
                                    value={outputState.outputMode === 'raw' ? outputState.responseText : ''}
                                    readOnly
                                    spellCheck={false}
                                    placeholder={t('The raw current value response will be shown here.')}
                                />
                            </div>
                        </section>
                    )}
                </>
            ) : null}
        </div>
    );
}

export default CurrentValueTab;
